import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteConfig } from './index';
import BasicLayout from '@/layouts/BasicLayout';
import {
  UsersIcon,
  LayoutDashboard,
  DollarSignIcon,
  UserRoundGroup,
  UsersRoundCollaboration,
} from 'lucide-react';
import { withSuspense } from './lazy';
import { DynamicRedirect } from './DynamicRedirect';
import { PERMISSIONS } from '@/constants/permissions';

// 业务页面
const WorkbenchPage = lazy(() => import('@/pages/Workbench'));

const AuthPage = lazy(() => import('@/pages/Auth'));
const ActivatePage = lazy(() => import('@/pages/Auth/Activate'));
const MemberManagement = lazy(() => import('@/pages/TeamManagement/MemberManagement'));
const QRCodeManagement = lazy(
  () => import('@/pages/TeamManagement/MemberManagement/QRCodeManagement')
);
const RoleManagement = lazy(() => import('@/pages/TeamManagement/RoleManagement'));
const CustomerFiles = lazy(() => import('@/pages/Customer/CustomerFiles/index'));
const CustomerFilesDetail = lazy(() => import('@/pages/Customer/CustomerFiles/detail'));
const AdAccountTabRoute = lazy(
  () => import('@/pages/Customer/CustomerFiles/ad-account/AdAccountTab')
);
const OpenAccountTabRoute = lazy(
  () => import('@/pages/Customer/CustomerFiles/open-account/OpenAccountTab')
);
const ContractTabRoute = lazy(() => import('@/pages/Customer/CustomerFiles/contract/ContractTab'));
const BusinessCenter = lazy(() => import('@/pages/Customer/BusinessCenter/index'));
const BusinessCenterDetail = lazy(() => import('@/pages/Customer/BusinessCenter/detail'));
const CreativeCenter = lazy(() => import('@/pages/Customer/CreativeCenter'));
const CreativeCenterDetail = lazy(() => import('@/pages/Customer/CreativeCenter/detail'));
const AssetManagement = lazy(() => import('@/pages/FinancialManagement/AssetManagement'));
const TikTokAssetManagementDetail = lazy(
  () => import('@/pages/FinancialManagement/AssetManagement/TikTokDetail')
);
const AssetGroupDetail = lazy(
  () => import('@/pages/FinancialManagement/AssetManagement/AssetGroupDetail')
);
const CreditApproval = lazy(() => import('@/pages/FinancialManagement/CreditApproval'));
const PerformanceManagement = lazy(() => import('@/pages/BusinessCenter/PerformanceManagement'));
const BusinessManagement = lazy(() => import('@/pages/BusinessCenter/BusinessManagement'));
const BusinessCustomerManagement = lazy(() => import('@/pages/BusinessCenter/CustomerManagement'));
const PerformanceHistory = lazy(
  () => import('@/pages/BusinessCenter/PerformanceManagement/PerformanceHistory')
);
const PerformanceDetail = lazy(
  () => import('@/pages/BusinessCenter/PerformanceManagement/PerformanceDetail')
);

const NotFound = lazy(() => import('@/pages/NotFound'));

/**
 * 路由配置数组
 * 支持嵌套路由结构
 */
export const routes: RouteConfig[] = [
  {
    path: '/',
    element: <BasicLayout />,
    children: [
      {
        path: '',
        element: (
          <DynamicRedirect
            targetRoute={{
              permCode: 'management:overview:menu',
              path: 'overview',
            }}
          />
        ),
      },
      // 工作台沿用原概览菜单权限与地址。
      {
        permCode: 'management:overview:menu',
        path: 'overview',
        element: withSuspense(WorkbenchPage),
        meta: {
          title: '工作台',
          icon: LayoutDashboard,
        },
      },
      {
        // TODO: 正式商务中心菜单权限下发前，复用客户管理权限。
        permCode: 'management:customer:menu',
        path: 'business',
        meta: { title: '商务中心', icon: UserRoundGroup },
        children: [
          { path: '', element: <Navigate to="/business/business-management" replace /> },
          {
            // TODO: 正式商务管理权限下发前，沿用商务中心现有的临时菜单权限。
            permCode: 'management:business_center:sub_menu',
            path: 'business-management',
            element: withSuspense(BusinessManagement),
            meta: { title: '商务管理' },
          },
          {
            // TODO: 正式商务客户管理权限下发前，沿用商务中心现有的临时菜单权限。
            permCode: 'management:business_center:sub_menu',
            path: 'customer-management',
            element: withSuspense(BusinessCustomerManagement),
            meta: { title: '客户管理' },
          },
          {
            // TODO: 正式业绩管理权限下发前，复用商业中心权限。
            permCode: 'management:business_center:sub_menu',
            path: 'performance-management',
            meta: { title: '业绩管理' },
            children: [
              { path: '', element: withSuspense(PerformanceManagement) },
              {
                path: 'history',
                meta: { title: '往期业绩' },
                children: [
                  { path: '', element: withSuspense(PerformanceHistory) },
                  {
                    path: ':periodId',
                    element: withSuspense(PerformanceDetail),
                    meta: { title: '业绩详情' },
                  },
                ],
              },
            ],
          },
        ],
      },
      // 客户管理 management:customer:menu
      {
        permCode: 'management:customer:menu',
        path: 'customer',
        meta: {
          title: '客户管理',
          icon: UsersRoundCollaboration,
        },
        children: [
          {
            path: '',
            element: <Navigate to="/customer/customer-files" />,
          },
          //客户档案
          {
            permCode: 'management:customer_profile:sub_menu',
            path: 'customer-files',
            meta: {
              title: '客户档案',
            },
            children: [
              {
                path: '',
                element: withSuspense(CustomerFiles),
              },
              {
                permCode: PERMISSIONS['客户管理/客户档案/客户档案详情'],
                path: 'detail/:customerId',
                element: withSuspense(CustomerFilesDetail),
                meta: {
                  title: '客户档案详情',
                  requireAuth: true,
                },
                children: [
                  {
                    permCode: PERMISSIONS['客户管理/客户档案/客户关联广告帐户分页列表'],
                    path: 'ad-account',
                    element: withSuspense(AdAccountTabRoute),
                    meta: {
                      title: '广告账户',
                      hideInBreadcrumb: true,
                    },
                  },
                  {
                    permCode: PERMISSIONS['客户管理/客户档案/客户开户主体分页列表'],
                    path: 'open-account',
                    element: withSuspense(OpenAccountTabRoute),
                    meta: {
                      title: '开户主体',
                      hideInBreadcrumb: true,
                    },
                  },
                  {
                    permCode: PERMISSIONS['客户管理/客户档案/合同分页列表'],
                    path: 'contract',
                    element: withSuspense(ContractTabRoute),
                    meta: {
                      title: '合同归档',
                      hideInBreadcrumb: true,
                    },
                  },
                  {
                    permCode: PERMISSIONS['客户管理/客户档案/合同分页列表'],
                    path: 'contract/:contractCode',
                    element: withSuspense(ContractTabRoute),
                    meta: {
                      title: '合同归档',
                      hideInBreadcrumb: true,
                    },
                  },
                ],
              },
            ],
          },
          {
            permCode: 'management:business_center:sub_menu',
            path: 'business-center',
            meta: {
              title: '商业中心',
            },
            children: [
              {
                path: '',
                element: withSuspense(BusinessCenter),
              },
              {
                permCode: PERMISSIONS['客户管理/商业中心/查看客户详情'],
                path: 'detail/:customerId',
                element: withSuspense(BusinessCenterDetail),
                meta: {
                  title: '客户详情',
                  requireAuth: true,
                },
              },
            ],
          },
          {
            permCode: 'management:creative_center:sub_menu',
            path: 'creative-center',
            meta: {
              title: '创意中心',
            },
            children: [
              {
                path: '',
                element: withSuspense(CreativeCenter),
              },
              {
                path: 'detail/:customerId',
                element: withSuspense(CreativeCenterDetail),
                meta: {
                  title: '创意中心客户详情',
                  requireAuth: true,
                },
              },
            ],
          },
        ],
      },
      // // 营销数据 management:marketing_data:menu
      // {
      //   // permCode: 'management:marketing_data:menu',
      //   path: 'marketing-data',
      //   meta: {
      //     title: '营销数据',
      //     icon: DatabaseIcon,
      //   },
      // },
      // 财务管理
      {
        permCode: 'management:finance:menu',
        path: 'finance',
        meta: {
          title: '财务管理',
          icon: DollarSignIcon,
        },
        children: [
          {
            path: '',
            element: <Navigate to="/finance/asset-management" />,
          },
          {
            permCode: 'management:finance:credit_approval:sub_menu', // TODO：待资产管理权限码下发后替换
            path: 'asset-management',
            meta: {
              title: '资产管理',
            },
            children: [
              {
                path: '',
                element: withSuspense(AssetManagement),
              },
              {
                path: 'tiktok',
                element: withSuspense(TikTokAssetManagementDetail),
                meta: {
                  title: 'TikTok资产详情',
                },
              },
              {
                permCode: 'management:finance:credit_approval:sub_menu', // TODO：待资产管理权限码下发后替换
                path: 'tiktok/group/:assetGroupId',
                element: withSuspense(AssetGroupDetail),
                meta: {
                  title: '资产组详情',
                },
              },
            ],
          },
          {
            permCode: 'management:finance:credit_approval:sub_menu',
            path: 'credit-approval',
            element: withSuspense(CreditApproval),
            meta: {
              title: '授信审批',
            },
          },
        ],
      },
      // 团队管理
      {
        permCode: 'management:team:menu',
        path: 'team-management',
        meta: {
          title: '团队管理',
          icon: UsersIcon,
        },
        children: [
          {
            path: '',
            element: <Navigate to="/team-management/member-management" />,
          },
          {
            permCode: 'management:member:sub_menu',
            path: 'member-management',
            meta: {
              title: '成员管理',
            },
            children: [
              {
                path: '',
                element: withSuspense(MemberManagement),
              },
              {
                permCode: 'management:member:sub_menu', //TODO：待替换
                path: 'qr-code-management',
                element: withSuspense(QRCodeManagement),
                meta: {
                  title: '二维码管理',
                },
              },
            ],
          },
          {
            permCode: 'management:role_permission:sub_menu',
            path: 'role-management',
            element: withSuspense(RoleManagement),
            meta: {
              title: '角色&权限',
            },
          },
        ],
      },
      // //设置 management:setting:menu
      // {
      //   // permCode: 'management:setting:menu',
      //   path: 'setting',
      //   meta: {
      //     title: '设置',
      //     icon: Settings2Icon,
      //   },
      //   children: [
      //     {
      //       path: '',
      //       element: <Navigate to="/team-management/member-management" />,
      //     },
      //     //日志
      //     {
      //       // permCode: 'management:log:sub_menu',
      //       path: 'log',
      //       meta: {
      //         title: '日志',
      //       },
      //       element: ,//TODO
      //     },
      //     //用户设置
      //     {
      //       // permCode: 'management:user_setting:sub_menu',
      //       path: 'user-setting',
      //       meta: {
      //         title: '用户设置',
      //       },
      //       element: ,//TODO
      //     },
      //   ],
      // },
    ],
  },
  {
    path: '/login',
    element: withSuspense(AuthPage),
    meta: {
      title: '登录',
      requireAuth: false,
    },
  },
  {
    path: '/activate',
    element: withSuspense(ActivatePage),
    meta: {
      title: '账号激活',
      requireAuth: false,
    },
  },
  {
    path: '/404',
    element: withSuspense(NotFound),
    meta: {
      title: '404',
      requireAuth: false,
    },
  },
  {
    path: '*',
    element: <Navigate to="/404" replace />,
    meta: {
      title: '未找到',
      requireAuth: false,
    },
  },
];

/**
 * 获取用户有权限访问的路由
 * @param permCodes 用户权限码数组(userStore获取)
 * @param data 路由数据(可选，默认使用routes)
 * @param filter 额外的路由过滤函数(可选，默认返回true)
 * @returns 有权限访问的路由数组
 */
export const getHasPermissionRoutes = (
  permCodes: string[],
  data?: RouteConfig[],
  filter: (route: RouteConfig) => boolean = () => true
): RouteConfig[] => {
  if (!permCodes.length) {
    const cloneRoute = (route: RouteConfig): RouteConfig => ({
      ...route,
      meta: route.meta ? { ...route.meta } : undefined,
      children: route.children ? route.children.map(cloneRoute) : undefined,
    });

    const loginRoute = routes.find((route) => route.path === '/login');
    const activateRoute = routes.find((route) => route.path === '/activate');
    const notFoundRoute = routes.find((route) => route.path === '/404');
    return [
      {
        path: '/',
        element: <Navigate to="/login" replace />,
      },
      ...(loginRoute ? [cloneRoute(loginRoute)] : []),
      ...(activateRoute ? [cloneRoute(activateRoute)] : []),
      ...(notFoundRoute ? [cloneRoute(notFoundRoute)] : []),
      {
        path: '*',
        element: <Navigate to="/login" replace />,
      },
    ];
  }
  const _routes: RouteConfig[] = data || routes;
  const eachArray = (routes: RouteConfig[]): RouteConfig[] => {
    return routes
      .map((route) => {
        // 创建新对象以避免修改原始路由配置
        const newRoute = { ...route };

        // 如果有 meta，也创建一个新对象，避免修改原始 meta
        if (newRoute.meta) {
          newRoute.meta = { ...newRoute.meta };
        }

        // 递归处理 children
        if (newRoute.children) {
          newRoute.children = eachArray(newRoute.children);
        }

        if (newRoute.permCode) {
          if (permCodes.includes(newRoute.permCode)) {
            return newRoute;
          }
        } else {
          // 没有权限码的路由视为非侧边栏导航跳转的路由，对于没有权限码的路由，不会在侧边栏导航中显示，但是可以通过直接访问路径来跳转
          return newRoute;
        }
      })
      .filter((r): r is RouteConfig => {
        if (!r) return false;
        return filter(r);
      });
  };
  return eachArray(_routes);
};
